import type { ReactNode } from "react";
import { cx } from "./cx";

export interface SwatchProps {
  /** A token REFERENCE, e.g. "var(--map-palette-categorical-1)" — never raw hex. */
  color: string;
  className?: string;
}

/** Legend swatch — 10px map data point with the structural white ring. */
export function Swatch({ color, className }: SwatchProps) {
  return <span className={cx("pz-swatch", className)} style={{ background: color }} aria-hidden />;
}

export interface LegendItem {
  color: string;
  label: ReactNode;
}

export interface LegendProps {
  title: ReactNode;
  items: LegendItem[];
  className?: string;
}

/** Map legend (map.chrome roles, §11) — white plate, mint hairline, radius.lg, elevation.2. */
export function Legend({ title, items, className }: LegendProps) {
  return (
    <div className={cx("pz-legend", className)}>
      <span className="pz-legend__title">{title}</span>
      {items.map((item, i) => (
        <span key={i} className="pz-legend__row">
          <Swatch color={item.color} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

export interface RampStripProps {
  /** Ordered token references, e.g. ["var(--map-ramp-heat-1)", ...]. */
  stops: string[];
  startLabel?: ReactNode;
  endLabel?: ReactNode;
  className?: string;
}

/** Continuous-ramp legend strip (heat / choropleth). */
export function RampStrip({ stops, startLabel, endLabel, className }: RampStripProps) {
  return (
    <span className={cx("pz-ramp", className)}>
      <span className="pz-ramp__strip" aria-hidden>
        {stops.map((stop, i) => (
          <span key={i} className="pz-ramp__cell" style={{ background: stop }} />
        ))}
      </span>
      {(startLabel != null || endLabel != null) && (
        <span className="pz-ramp__labels">
          <span>{startLabel}</span>
          <span>{endLabel}</span>
        </span>
      )}
    </span>
  );
}
